import { apiFetch } from '@/lib/http/client'
import type {
  WorkspaceInfo,
  WorkspaceMember,
  AllowedDomain,
  PendingInvitation,
  InvitationRole,
} from '@/lib/workspace/types/types'
import type { BillingInterval } from '@/lib/billing/types'

export interface CreateWorkspaceRequest {
  name: string
  type: 'team'  // personal is auto-created at signup; client never creates personal
  /** Billing cadence for the team subscription — 'month' (default) or 'year'. */
  interval?: BillingInterval
  /** The chosen team tier — 'teams_plus' or 'teams_pro' (default). */
  planCode?: string
  /** Campaign code applied at creation. Referral codes are personal-only and are rejected here. */
  promotionCode?: string
}

// POST /api/workspaces returns the created workspace plus the embedded
// Payment Element clientSecret (design §11.5 #6/#7). For a team, clientSecret
// gates the paywall; personal creation returns an empty string.
export interface CreateWorkspaceResponse {
  workspace: WorkspaceInfo
  clientSecret: string
}

export interface UpdateWorkspaceRequest {
  name?: string
  // memberDefaultPermission is reserved for a future Defaults tab — the current
  // General tab does NOT touch this field.
  memberDefaultPermission?: 'admin' | 'edit' | 'read'
  /** Toggle: auto-join new signups whose email domain matches an allowed-domain row. */
  autoJoinEnabled?: boolean
}

// Note: `apiFetch` prepends `/api` internally. These BASE paths are unprefixed.
const BASE = '/workspaces'

export async function listWorkspaces(): Promise<WorkspaceInfo[]> {
  return apiFetch<WorkspaceInfo[]>(BASE)
}

// Note: there is no `GET /workspaces/{id}` route on the server today — only
// PATCH/DELETE. `listWorkspaces()` already returns all workspaces the user
// belongs to with full row data, so individual fetch is unnecessary now.

export async function listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  return apiFetch<WorkspaceMember[]>(`${BASE}/${workspaceId}/members`)
}

export async function listAllowedDomains(workspaceId: string): Promise<AllowedDomain[]> {
  return apiFetch<AllowedDomain[]>(`${BASE}/${workspaceId}/allowed-domains`)
}

export async function listPendingInvitations(workspaceId: string): Promise<PendingInvitation[]> {
  return apiFetch<PendingInvitation[]>(`${BASE}/${workspaceId}/invitations`)
}

export async function createWorkspace(req: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
  return apiFetch<CreateWorkspaceResponse>(BASE, {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

export async function updateWorkspace(workspaceId: string, req: UpdateWorkspaceRequest): Promise<void> {
  await apiFetch(`${BASE}/${workspaceId}`, {
    method: 'PATCH',
    body: JSON.stringify(req),
  })
}

export async function setMemberRole(
  workspaceId: string,
  userId: string,
  role: 'admin' | 'member',
): Promise<void> {
  await apiFetch(`${BASE}/${workspaceId}/members/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  })
}

export async function removeMember(workspaceId: string, userId: string): Promise<void> {
  await apiFetch(`${BASE}/${workspaceId}/members/${userId}`, {
    method: 'DELETE',
  })
}

export async function revokeInvitation(workspaceId: string, invitationId: string): Promise<void> {
  await apiFetch(`${BASE}/${workspaceId}/invitations/${invitationId}`, { method: 'DELETE' })
}

export async function addAllowedDomain(workspaceId: string, domain: string): Promise<void> {
  await apiFetch(`${BASE}/${workspaceId}/allowed-domains`, {
    method: 'POST',
    body: JSON.stringify({ domain }),
  })
}

export async function removeAllowedDomain(workspaceId: string, domain: string): Promise<void> {
  await apiFetch(`${BASE}/${workspaceId}/allowed-domains/${encodeURIComponent(domain)}`, { method: 'DELETE' })
}

export async function inviteByEmail(workspaceId: string, email: string, role: InvitationRole): Promise<void> {
  await apiFetch<undefined>(`${BASE}/${workspaceId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  })
}

// NOTE: path does NOT include `/api` — apiFetch prepends `${VITE_API_URL}/api`.
export async function acceptInviteLink(token: string): Promise<{ workspaceId: string }> {
  return apiFetch<{ workspaceId: string }>('/invitations/accept-link', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

export async function transferOwnership(workspaceId: string, toUserId: string): Promise<void> {
  await apiFetch<undefined>(`${BASE}/${workspaceId}/transfer-ownership`, {
    method: 'POST',
    body: JSON.stringify({ toUserId }),
  })
}

export async function deleteWorkspace(workspaceId: string): Promise<void> {
  await apiFetch<undefined>(`${BASE}/${workspaceId}`, { method: 'DELETE' })
}
